import { Box, Button, FormControlLabel, List, ListItem, ListItemText, Paper, Radio, RadioGroup, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { useHttpsCallable } from 'src/utils/firebase'
import { capture } from 'src/utils/sentry'

type Interval = 'monthly' | 'yearly'
type PaymentMethod = 'card' | 'bank_transfer'

type Props = {
  clientId: string
  isAdmin: boolean
}

export const ProUpgradeNotice = ({ clientId, isAdmin }: Props) => {
  const [interval, setInterval] = useState<Interval>('monthly')
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card')

  const [callCheckout, isCallingCheckout] = useHttpsCallable<{ clientId: string; interval: Interval }, { url: string }>('createProCheckoutSession')
  const [callBankTransfer, isCallingBankTransfer] = useHttpsCallable<
    { clientId: string; interval: Interval },
    { subscriptionId: string; invoiceUrl: string | null }
  >('createProBankTransferSubscription')

  const isCalling = isCallingCheckout || isCallingBankTransfer

  const handleUpgrade = async () => {
    try {
      if (paymentMethod === 'card') {
        const res = await callCheckout({ clientId, interval })
        if (res?.data?.url) {
          window.location.href = res.data.url
        } else {
          toast.error('チェックアウト URL の取得に失敗しました')
        }
      } else {
        const res = await callBankTransfer({ clientId, interval })
        if (res?.data?.invoiceUrl) {
          window.location.href = res.data.invoiceUrl
        } else {
          toast.error('請求書の URL の取得に失敗しました')
        }
      }
    } catch (e) {
      capture(e)
      toast.error('アップグレードの開始に失敗しました')
    }
  }

  return (
    <Paper elevation={6} sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Pro プランで使える機能</Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="サブグループの作成 / 編集" secondary="デバイスとメンバーをまとめて、通知の振り分け先を設定できます。" />
          </ListItem>
          <ListItem>
            <ListItemText primary="LINEグループ単位の通知" secondary="各サブグループに専用のLINEトークルームを紐づけて、まとめて通知できます。" />
          </ListItem>
        </List>
        {isAdmin ? (
          <>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                お支払い周期
              </Typography>
              <RadioGroup row value={interval} onChange={(_, v) => setInterval(v as Interval)}>
                <FormControlLabel value="monthly" control={<Radio />} label="月額" />
                <FormControlLabel value="yearly" control={<Radio />} label="年額" />
              </RadioGroup>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                お支払い方法
              </Typography>
              <RadioGroup value={paymentMethod} onChange={(_, v) => setPaymentMethod(v as PaymentMethod)}>
                <FormControlLabel value="card" control={<Radio />} label="カード (自動更新)" />
                <FormControlLabel value="bank_transfer" control={<Radio />} label="銀行振込 (請求書送付)" />
              </RadioGroup>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button variant="contained" disabled={isCalling} onClick={handleUpgrade}>
                {paymentMethod === 'card' ? 'Pro プランにアップグレード' : '請求書を発行する'}
              </Button>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              p: 2,
              backgroundColor: 'info.light',
              borderRadius: 1,
            }}
          >
            <Typography>Pro プランへのお申し込みはグループの管理者のみ可能です。所属グループの管理者にお問合せください。</Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  )
}
